/**
 * ContextualSideNav — Glass-Forward side navigation shell.
 * Story 13.2: Route-context-aware, RBAC-filtered, collapsible side nav.
 *
 * Features:
 * - Route-based context switching with crossfade animation
 * - RBAC permission filtering (items hidden, not disabled)
 * - Collapsed (64px icons-only) and expanded (256px) states
 * - localStorage persistence via ui-store
 * - Tablet overlay and mobile full-screen overlay
 * - Active item highlighting with brand-primary left border
 */

import { useMemo, useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Home } from "lucide-react";
import { useUIStore } from "../../stores/ui-store";
import { useAuth } from "../../hooks/use-auth";
import {
  deriveNavContext,
  NAV_SECTIONS_BY_CONTEXT,
  filterNavItems,
  isActiveItem,
  type NavSectionConfig,
} from "./side-nav-config";
import { NavSection } from "./side-nav/NavSection";

// ============================================================
// Mobile/Tablet Detection Hook
// ============================================================

function useMobileNav() {
  const [isTablet, setIsTablet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  useEffect(() => {
    const tabletQuery = window.matchMedia(
      "(min-width: 640px) and (max-width: 1023px)",
    );
    const mobileQuery = window.matchMedia("(max-width: 639px)");

    const handleTabletChange = (e: MediaQueryListEvent) =>
      setIsTablet(e.matches);
    const handleMobileChange = (e: MediaQueryListEvent) =>
      setIsMobile(e.matches);

    setIsTablet(tabletQuery.matches);
    setIsMobile(mobileQuery.matches);

    tabletQuery.addEventListener("change", handleTabletChange);
    mobileQuery.addEventListener("change", handleMobileChange);

    return () => {
      tabletQuery.removeEventListener("change", handleTabletChange);
      mobileQuery.removeEventListener("change", handleMobileChange);
    };
  }, []);

  return { isTablet, isMobile, isOverlayOpen, setIsOverlayOpen };
}

// ============================================================
// ContextualSideNav Component
// ============================================================

interface ContextualSideNavProps {
  /** Current pathname from React Router useLocation().pathname */
  currentPath: string;
  /** Navigation handler — typically from React Router useNavigate() */
  onNavigate: (path: string) => void;
  /** Optional badge counts keyed by nav item id */
  badgeCounts?: Record<string, number>;
}

export function ContextualSideNav({
  currentPath,
  onNavigate,
  badgeCounts,
}: ContextualSideNavProps) {
  const { sideNavCollapsed, toggleSideNav } = useUIStore();
  const { user } = useAuth();
  const { isTablet, isMobile, isOverlayOpen, setIsOverlayOpen } =
    useMobileNav();

  const userPermissions = user?.permissions ?? [];

  // Derive nav context from current route
  const navContext = useMemo(
    () => deriveNavContext(currentPath),
    [currentPath],
  );

  // Get sections for current context, filtered by RBAC
  const filteredSections = useMemo(() => {
    const sections = NAV_SECTIONS_BY_CONTEXT[navContext] ?? [];
    return sections
      .map((section: NavSectionConfig) => ({
        ...section,
        items: filterNavItems(section.items, userPermissions),
      }))
      .filter((section) => section.items.length > 0);
  }, [navContext, userPermissions]);

  // Close overlay on route change
  useEffect(() => {
    setIsOverlayOpen(false);
  }, [currentPath, setIsOverlayOpen]);

  // Close overlay on Escape
  useEffect(() => {
    if (!isOverlayOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOverlayOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOverlayOpen, setIsOverlayOpen]);

  const handleNavigate = useCallback(
    (path: string) => {
      onNavigate(path);
      if (isTablet || isMobile) {
        setIsOverlayOpen(false);
      }
    },
    [onNavigate, isTablet, isMobile, setIsOverlayOpen],
  );

  // ── Nav Content ──
  const navContent = (
    <>
      {/* Logo / collapse toggle area */}
      <div className="flex items-center h-14 px-4 border-b border-[var(--surface-glass-border)]">
        <button
          onClick={() => handleNavigate("/")}
          className="flex items-center gap-2 text-sm font-semibold text-white transition-interactive hover:text-[var(--brand-primary)]"
          aria-label="Go to home"
        >
          <Home size={18} aria-hidden="true" />
          {!sideNavCollapsed && !(isTablet || isMobile) && (
            <span className="side-nav-label">Zero Touch Data</span>
          )}
          {(isTablet || isMobile) && <span>Zero Touch Data</span>}
        </button>

        {/* Close button for mobile overlay */}
        {(isTablet || isMobile) && isOverlayOpen && (
          <button
            onClick={() => setIsOverlayOpen(false)}
            className="ml-auto text-[var(--text-muted)] hover:text-white transition-interactive"
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav sections with context animation */}
      <div
        className="flex-1 overflow-y-auto py-2 side-nav-context-enter"
        key={navContext}
      >
        {filteredSections.map((section) => (
          <NavSection
            key={section.id}
            label={section.label}
            items={section.items}
            currentPath={currentPath}
            isCollapsed={sideNavCollapsed && !(isTablet || isMobile)}
            isActiveItem={isActiveItem}
            onNavigate={handleNavigate}
            badgeCounts={badgeCounts}
          />
        ))}
      </div>

      {/* Collapse/Expand toggle (desktop only) */}
      {!(isTablet || isMobile) && (
        <div className="border-t border-[var(--surface-glass-border)] p-2">
          <button
            onClick={toggleSideNav}
            className="flex items-center justify-center w-full py-2 rounded-md text-[var(--text-muted)] hover:text-white hover:bg-white/5 transition-interactive"
            aria-label={
              sideNavCollapsed ? "Expand navigation" : "Collapse navigation"
            }
          >
            {sideNavCollapsed ? (
              <ChevronRight size={16} />
            ) : (
              <ChevronLeft size={16} />
            )}
          </button>
        </div>
      )}
    </>
  );

  // ── Desktop Rendering ──
  if (!isTablet && !isMobile) {
    return (
      <nav
        className={`side-nav glass-chrome flex flex-col h-full ${
          sideNavCollapsed ? "w-16" : "w-64"
        }`}
        data-collapsed={sideNavCollapsed ? "true" : "false"}
        aria-label="Main navigation"
      >
        {navContent}
      </nav>
    );
  }

  // ── Tablet / Mobile Overlay Rendering ──
  return (
    <>
      {/* Backdrop */}
      <div
        className="side-nav-overlay-backdrop"
        data-open={isOverlayOpen ? "true" : "false"}
        onClick={() => setIsOverlayOpen(false)}
        aria-hidden="true"
      />

      {/* Overlay nav */}
      <nav
        className="side-nav-overlay glass-chrome flex flex-col"
        data-open={isOverlayOpen ? "true" : "false"}
        aria-label="Main navigation"
        aria-hidden={!isOverlayOpen}
      >
        {navContent}
      </nav>
    </>
  );
}

/**
 * Hamburger menu trigger for tablet/mobile — include in top bar.
 */
export function SideNavMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md text-[var(--text-secondary)] hover:text-white hover:bg-white/5 transition-interactive"
      aria-label="Open navigation menu"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <line x1="3" y1="5" x2="15" y2="5" />
        <line x1="3" y1="9" x2="15" y2="9" />
        <line x1="3" y1="13" x2="15" y2="13" />
      </svg>
    </button>
  );
}
